import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { TOURS } from "./tours";

const STORAGE_KEY = "fk_tour_prefs";
// Session-scoped guard: only ONE tour may auto-start per browser session.
const AUTOSTART_SESSION_KEY = "fk_tour_autostart_done";

const DEFAULT_PREFS = {
  completedTours: [],
  globallyDisabled: false,
};

// Prefs are scoped per logged-in user so a shared browser doesn't leak
// one user's "seen it" state onto another.
function prefsKeyFor(userId) {
  return `${STORAGE_KEY}:${userId || "anon"}`;
}

function loadPrefs(userId) {
  try {
    const raw = localStorage.getItem(prefsKeyFor(userId));
    if (raw) return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
    // One-time migration: fall back to the legacy shared (un-suffixed) key.
    const legacy = localStorage.getItem(STORAGE_KEY);
    if (legacy) {
      const migrated = { ...DEFAULT_PREFS, ...JSON.parse(legacy) };
      persistPrefs(migrated, userId);
      return migrated;
    }
    return DEFAULT_PREFS;
  } catch {
    return DEFAULT_PREFS;
  }
}

function persistPrefs(prefs, userId) {
  try {
    localStorage.setItem(prefsKeyFor(userId), JSON.stringify(prefs));
  } catch {}
}

// Module-level fallback in case sessionStorage is unavailable (private mode, etc.)
let autoStartedThisLoad = false;

function hasAutoStartedThisSession() {
  if (autoStartedThisLoad) return true;
  try {
    return sessionStorage.getItem(AUTOSTART_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

function markAutoStartedThisSession() {
  autoStartedThisLoad = true;
  try {
    sessionStorage.setItem(AUTOSTART_SESSION_KEY, "1");
  } catch {}
}

const WalkthroughContext = createContext(null);

export function WalkthroughProvider({ children }) {
  // Provider is mounted inside the redux <Provider> (see index.js), so
  // useSelector is safe here.
  const userId = useSelector((s) => s?.reducer?.userLoggedIn?._id);
  const userIdRef = useRef(userId);
  const [prefs, setPrefs] = useState(() => loadPrefs(userId));
  const [activeTour, setActiveTour] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  // Queue of tours to start after current one finishes
  const queueRef = useRef([]);

  // Reload prefs whenever the logged-in user changes (login/logout/switch).
  useEffect(() => {
    if (userIdRef.current === userId) return;
    userIdRef.current = userId;
    setPrefs(loadPrefs(userId));
  }, [userId]);

  const updatePrefs = useCallback((updater) => {
    setPrefs((prev) => {
      const next =
        typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
      persistPrefs(next, userIdRef.current);
      return next;
    });
  }, []);

  const activeTourConfig = activeTour ? TOURS[activeTour] : null;
  const steps = activeTourConfig?.steps ?? [];
  const totalSteps = steps.length;

  const emit = useCallback((event, detail = {}) => {
    window.dispatchEvent(new CustomEvent(`fk:tour:${event}`, { detail }));
  }, []);

  // Drain the queue when no tour is active
  useEffect(() => {
    if (!activeTour && queueRef.current.length > 0) {
      const next = queueRef.current.shift();
      setActiveTour(next);
      setCurrentStep(0);
    }
  }, [activeTour]);

  const startTour = useCallback(
    (tourId, { force = false } = {}) => {
      if (prefs.globallyDisabled && !force) return;
      if (prefs.completedTours.includes(tourId) && !force) return;
      if (!TOURS[tourId]) return;

      if (activeTour) {
        // defer until current tour ends
        if (!queueRef.current.includes(tourId)) queueRef.current.push(tourId);
        return;
      }

      setActiveTour(tourId);
      setCurrentStep(0);
      emit("start", { tourId });
    },
    [prefs, activeTour, emit]
  );

  const closeTour = useCallback(
    (reason = "close") => {
      if (activeTour) emit("close", { tourId: activeTour, step: currentStep, reason });
      setActiveTour(null);
      setCurrentStep(0);
    },
    [activeTour, currentStep, emit]
  );

  const nextStep = useCallback(() => {
    if (currentStep >= totalSteps - 1) {
      updatePrefs((prev) => ({
        ...prev,
        completedTours: [...new Set([...prev.completedTours, activeTour])],
      }));
      emit("complete", { tourId: activeTour });
      setActiveTour(null);
      setCurrentStep(0);
    } else {
      const next = currentStep + 1;
      setCurrentStep(next);
      // Defer so listeners (e.g. switch lane) run after React commits; avoids spotlight jumping to center.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          emit("step", { tourId: activeTour, step: next });
        });
      });
    }
  }, [currentStep, totalSteps, activeTour, updatePrefs, emit]);

  const goToStep = useCallback((index) => {
    if (index < 0 || index >= totalSteps) return;
    setCurrentStep(index);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        emit("step", { tourId: activeTour, step: index });
      });
    });
  }, [totalSteps, activeTour, emit]);

  const prevStep = useCallback(() => {
    setCurrentStep((s) => {
      const next = Math.max(0, s - 1);
      if (next !== s) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            emit("step", { tourId: activeTour, step: next });
          });
        });
      }
      return next;
    });
  }, [activeTour, emit]);

  const skipTour = useCallback(
    (neverShow = false) => {
      if (neverShow && activeTour) {
        updatePrefs((prev) => ({
          ...prev,
          completedTours: [...new Set([...prev.completedTours, activeTour])],
        }));
      }
      emit("skip", { tourId: activeTour, step: currentStep, neverShow });
      closeTour("skip");
    },
    [activeTour, currentStep, updatePrefs, closeTour, emit]
  );

  const disableAll = useCallback(() => {
    updatePrefs({ globallyDisabled: true });
    closeTour("disabled");
  }, [updatePrefs, closeTour]);

  const enableAll = useCallback(() => {
    updatePrefs({ globallyDisabled: false });
  }, [updatePrefs]);

  const resetTour = useCallback(
    (tourId) => {
      updatePrefs((prev) => ({
        ...prev,
        completedTours: prev.completedTours.filter((id) => id !== tourId),
        globallyDisabled: false,
      }));
    },
    [updatePrefs]
  );

  const resetAllTours = useCallback(() => {
    updatePrefs({ completedTours: [], globallyDisabled: false });
  }, [updatePrefs]);

  const isTourCompleted = useCallback(
    (tourId) => prefs.completedTours.includes(tourId),
    [prefs.completedTours]
  );

  return (
    <WalkthroughContext.Provider
      value={{
        activeTour,
        currentStep,
        totalSteps,
        activeTourConfig,
        steps,
        prefs,
        startTour,
        nextStep,
        prevStep,
        goToStep,
        skipTour,
        closeTour,
        disableAll,
        enableAll,
        resetTour,
        resetAllTours,
        isTourCompleted,
      }}
    >
      {children}
    </WalkthroughContext.Provider>
  );
}

export function useWalkthrough() {
  const ctx = useContext(WalkthroughContext);
  if (!ctx) throw new Error("useWalkthrough must be used inside WalkthroughProvider");
  return ctx;
}

/**
 * Drop into any page component to auto-start its tour on first visit.
 *
 *   useTourAutoStart('dashboard');
 *
 * The tour fires once, after a short delay to let the page render.
 *
 * At most ONE tour auto-starts per browser session (the first page the user
 * lands on). Manual starts via startTour(id, { force: true }) — e.g. the
 * header "?" button — bypass this guard entirely.
 */
export function useTourAutoStart(tourId, delayMs = 600) {
  const { startTour, isTourCompleted, prefs } = useWalkthrough();

  useEffect(() => {
    if (!tourId) return;
    if (prefs.globallyDisabled) return;
    if (isTourCompleted(tourId)) return;
    if (hasAutoStartedThisSession()) return;
    const timer = setTimeout(() => {
      if (hasAutoStartedThisSession()) return;
      markAutoStartedThisSession();
      startTour(tourId);
    }, delayMs);
    return () => clearTimeout(timer);
  }, [tourId]); // intentionally only tourId — startTour is stable but we don't want re-fires
}
